import { NgClass } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IZone } from '@webapp/core/interfaces/zones.interface';
import { ZonesService } from '@webapp/core/services/zones.service';
import {
  ButtonComponent,
  ButtonSwitchComponent,
  CardComponent,
  IActivarButtonSwitch,
  InputErrorComponent,
} from '@webapp/ui';
import { QuoterStoreService } from '../../../store/quoter-store.service';
import { IUser, USER_ROLES, UserRole } from '@webapp/users/interfaces/users.interface';
import { UsersService } from '@webapp/users/services/users.service';
import { RiskRegistry } from '@webapp/risk-core';
import { CHANNEL, CHANNEL_QUOTE_ENUM } from '@webapp/core/config';
import { NgxMaskDirective } from 'ngx-mask';
import { CuitBlurNormalizeDirective, cuitValidator } from '@webapp/core';

@Component({
  selector: 'app-create-user-form',
  standalone: true,
  imports: [
    ButtonComponent,
    InputErrorComponent,
    ReactiveFormsModule,
    CardComponent,
    NgClass,
    ButtonSwitchComponent,
    NgxMaskDirective,
    CuitBlurNormalizeDirective,
  ],
  templateUrl: './create-user-form.component.html',
  styleUrl: './create-user-form.component.scss',
})
export class CreateUserFormComponent implements OnInit {
  private readonly quoterStateService = inject(QuoterStoreService);
  readonly quoterState = this.quoterStateService.state.asReadonly();
  private channel = inject(CHANNEL);
  isSubmitted = false;
  buttonText = 'Continuar';
  private zonesService = inject(ZonesService);
  private usersService = inject(UsersService);
  private riskRegistry = inject(RiskRegistry);
  userForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    businessName: new FormControl(''),
    email: new FormControl('', [Validators.required, Validators.email]),
    postalCode: new FormControl(
      '',
      this.hasRiskPostalCode(this.quoterStateService.state().risk.tag)
        ? [Validators.required, Validators.pattern(/^[0-9]*$/)]
        : [],
    ),
    identification: new FormControl('', [cuitValidator]),
  });
  loading = signal(false);
  userTypes = input<string>();
  zones = signal<IZone[]>([]);
  selectedZone = signal<string>('');
  EventFormSubmit = output<IUser>();
  clientType = signal<string>('person');

  buttonConfigs: Record<string, IActivarButtonSwitch> = {
    person: { name: 'Persona', id: 1, type: 'person' },
    company: { name: 'Empresa', id: 2, type: 'company' },
  };
  buttons = Object.values(this.buttonConfigs);

  ngOnInit(): void {
    if (this.userTypes()) {
      this.clientType.set(this.userTypes() ?? 'person');
      this.syncPersonValidators();
    }
  }

  getEmailLabel() {
    return (this.channel === CHANNEL_QUOTE_ENUM.AGENTS) ? 'Email del asegurado' : 'Email';
  }

  isPostalCodeValidated() {
    return this.hasRiskPostalCode(this.quoterState().risk.tag) && !this.selectedZone();
  }


  setClientType(e: IActivarButtonSwitch) {
    if (e.type) {
      this.clientType.set(e.type);
    }

    this.syncPersonValidators();
  }

  private syncPersonValidators() {
    const { firstName, lastName } = this.userForm.controls;
    const validators =
      this.clientType() === 'person' ? [Validators.required] : null;
    firstName.setValidators(validators);
    lastName.setValidators(validators);
    firstName.updateValueAndValidity();
    lastName.updateValueAndValidity();
  }

  hasRiskPostalCode(risk: string) {
    return this.riskRegistry.find(risk)?.capabilities.requiresZone ?? false;
  }

  checkPostalCode() {
    this.loading.set(true);
    this.zonesService
      .getZones(this.userForm.controls['postalCode'].value)
      .subscribe({
        next: (response) => {

          const zones = response;
          if (zones.length == 1) {
            this.selectedZone.set(zones[0].id);
          } else {
            this.zones.set(zones);
          }
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  setZone(zone: IZone) {
    this.selectedZone.set(zone.id);
  }

  onSubmitHandler() {
    this.isSubmitted = true;
    if (!this.userForm.valid) return;

    const email: string = this.userForm.controls['email'].value;
    const user: IUser = {
      firstName: this.userForm.controls['firstName'].value,
      lastName: this.userForm.controls['lastName'].value,
      companyName: this.userForm.controls['businessName'].value,
      email,
      postalCode: this.userForm.controls['postalCode'].value,
      identification: this.userForm.controls['identification'].value,
      zoneId: this.selectedZone(),
      type: this.clientType().toUpperCase(),
    };

    this.loading.set(true);
    this.usersService.check(email).subscribe({
      next: (result) => {
        this.loading.set(false);
       
        if (result.exists && !result.isProspect) {
          user.role = UserRole.CLIENT;
          user.isClient = true;
        }
        this.EventFormSubmit.emit(user);
      },
      error: () => {
        this.loading.set(false);
        this.EventFormSubmit.emit(user);
      },
    });
  }
}
